import { captureException } from "@sentry/react";

import { getResponseStatus } from "~/utils/sdk-errors";

/**
 * Canonical reporter for fire-and-forget async failures — anything caught from a promise
 * the UI is not awaiting (preload, prefetch, optimistic background sync, dev-mode dynamic
 * imports, etc). Use instead of bare `.catch(() => {})` or `.catch(console.error)`.
 *
 * Routes to Sentry with a stable `fire_and_forget_label` tag so dashboards can group by
 * label. In dev, mirrors to `console.error` so failures surface during local work.
 *
 * Network errors (any HTTP status from the SDK) are dropped — those flows are already
 * covered by backend monitoring, and a degraded backend would otherwise generate a
 * hover-storm of duplicate captures from prefetch paths.
 */
export function reportAsyncError(label: string, error: unknown, context?: Record<string, unknown>): void {
  if (import.meta.env.DEV) {
    console.error(`[fire-and-forget:${label}]`, error, context);
  }

  if (getResponseStatus(error) !== undefined) return;

  captureException(error, {
    tags: { fire_and_forget_label: label },
    extra: context,
  });
}
